import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shopConfig as cfg } from '../shopConfig';
import { fetchProducts, cachedProducts } from '../productsApi';
import { useBrand } from '../hooks/useBrand';

// SmartShop standard image cycle (copied from shop-0001): hover -> zoom + flip.
// Only the COUNT matters here - the hook cycles an index, it never reads the items.
// Typed loosely so callers can pass URLs or {small, medium} srcset pairs.
const useImageCycle = (images: unknown[], active: boolean, interval = 2000) => {
  const [currentImg, setCurrentImg] = React.useState(0);
  const [zoomed, setZoomed] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  React.useEffect(() => {
    if (!active || images.length < 2) {
      if (active && images.length >= 1) setZoomed(true);
      return;
    }
    setZoomed(true);
    timerRef.current = setInterval(() => {
      setZoomed(false);
      setTimeout(() => { setCurrentImg(prev => (prev + 1) % images.length); setZoomed(true); }, 300);
    }, interval);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [active, images.length, interval]);
  React.useEffect(() => {
    if (!active) { if (timerRef.current) clearInterval(timerRef.current); setCurrentImg(0); setZoomed(false); }
  }, [active]);
  return { currentImg, zoomed };
};

// Phase-3 storefront search. Glen's searches product NAMES only, which is thin once a
// catalogue has categories and brand collections: a shopper typing "kaffe" or "dress"
// expects to find things whether that word is the name, the category or the brand.
//
// Every term must match somewhere (AND, not OR), so "kaffe dress" narrows rather than
// returning everything Kaffe plus every dress. Accents are folded, so "rose" finds
// "Rosé" - a real case in clothing.
const fold = (s: string) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function matchesSearch(p: Product, query: string): boolean {
  const terms = fold(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = fold([
    p.name,
    p.category,
    ...(p.collections || []),
  ].filter(Boolean).join(' '));
  return terms.every((t) => haystack.includes(t));
}

// -----------------------------------------------------------------------------
// ShopPage.tsx - the product grid. The centrepiece the customer sees first.
//
// Reads smartshop-list-products for the tenant's gido; the backend normaliser
// already flattens each product's image to a URL string (primary_image), which
// for a migrated tenant is the published {small} rendition (fast) or the source
// URL while a rendition is still pending. Mobile-first responsive grid (62% of
// Red Robin's visitors are mobile): 2 columns on phones, up to 4 on desktop.
//
// Filtering: the route param decides what's shown.
//   /shop                      -> everything
//   /shop/:category            -> products whose category slug matches
//   /collections/:collection   -> products whose collections[] contains it
// Category/collection come in as slugs; we match against the product fields.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------

const slugify = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const money = (cents: number | null, ccy = 'EUR') =>
  cents == null ? '' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: ccy }).format(cents / 100);

interface Product {
  gid: string;
  product_id: string;
  name: string;
  slug: string;
  category: string;
  collections?: string[];
  price: number | null;        // euros (float) from the compat response
  price_cents: number | null;
  currency: string;
  price_type: string;
  primary_image: string;
  secondary_image?: string;
  all_assets?: string[];
  primary_image_small?: string;
  primary_image_medium?: string;
  secondary_image_small?: string;
  secondary_image_medium?: string;
  stock_status: string;
  variants: any[];
}

const ProductCard: React.FC<{ p: Product; accent: string; navigate: (path: string) => void }> = ({ p, accent, navigate }) => {
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  const soldOut = p.stock_status === 'out_of_stock';
  // build the image set exactly like shop-0001: primary + secondary + all_assets, deduped, max 6
  // Each card image as a {small, medium} pair so the browser can pick per device.
  // Falls back to the single resolved URL for products without renditions (Glen).
  const cardImages = [
    { small: p.primary_image_small || p.primary_image, medium: p.primary_image_medium || p.primary_image },
    { small: p.secondary_image_small || p.secondary_image, medium: p.secondary_image_medium || p.secondary_image },
    ...(p.all_assets || []).map(a => ({ small: a, medium: a })),
  ].filter((im, i, arr): im is { small: string; medium: string } =>
      !!im.small && arr.findIndex(x => x.small === im.small) === i).slice(0, 6);
  // mobile only (no hover): animate when the card is ~fully in view (Phase-3 standard)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches;
  const imgRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node || !isTouch) return;
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0].isIntersecting),
      { threshold: 0.9 }
    );
    obs.observe(node);
  }, [isTouch]);
  const active = hovered || (isTouch && inView);
  const { currentImg, zoomed } = useImageCycle(cardImages, active, 2000);

  return (
    <div className="prod-card" onClick={() => navigate(`/product/${p.slug}`)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div ref={imgRef} style={{ position: 'relative', overflow: 'hidden' }} className="prod-img">
        {cardImages.length
          ? (active ? cardImages : cardImages.slice(0, 1)).map((img, i) => (
              <img key={i} src={img.medium || img.small} alt={p.name}
                srcSet={img.medium && img.medium !== img.small
                  ? `${img.small} 350w, ${img.medium} 800w` : undefined}
                sizes="(min-width:960px) 25vw, (min-width:640px) 33vw, 50vw"
                loading={i === 0 ? 'eager' : 'lazy'} decoding="async"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                  opacity: currentImg === i ? 1 : 0,
                  transform: currentImg === i && zoomed ? 'scale(1.08)' : 'scale(1)',
                  transition: 'opacity 0.3s ease-in-out, transform 2s ease-in-out',
                  zIndex: currentImg === i ? 1 : 0 }} />
            ))
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(0,0,0,.25)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em' }}>{cfg.shop_name}</div>}
        {soldOut && (
          <span style={{ position: 'absolute', top: 10, left: 10, background: cfg.brand.secondary, color: '#fff', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', padding: '4px 8px', zIndex: 3 }}>Sold Out</span>
        )}
      </div>
      <div style={{ padding: '10px 12px 14px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(0,0,0,.4)' }}>{p.category}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: cfg.brand.secondary, lineHeight: 1.3, flex: 1 }}>{p.name}</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: accent, marginTop: 2 }}>
          {p.price_type === 'variable' ? 'Gift Card' : money(p.price_cents, p.currency)}
        </span>
      </div>
    </div>
  );
};

export const ShopPage: React.FC<{ mode?: 'category' | 'collection' | 'group' }> = ({ mode = 'category' }) => {
  const brand = useBrand();
  const accent = brand.accent || cfg.brand.accent;
  const navigate = useNavigate();
  const params = useParams();
  const filterSlug = params.category || params.collection || params.group || '';

  // Seed from the session cache so the grid paints at once on a repeat visit or a
  // navigation back from a product. Without this, productsApi writes a cache nobody
  // reads, and every entry to the shop waits ~2.5s for the fetch before the first image
  // can even begin loading - which is what put LCP at 5.10s.
  const seeded = cachedProducts('grid');
  const [products, setProducts] = useState<Product[]>((seeded as Product[]) || []);
  const [loading, setLoading] = useState(!seeded);
  const [error, setError] = useState(false);

    // The search term lives in the URL (?q=), not only in state. A browser restores a
  // history entry's URL, never a component's state - so searching, opening a product
  // and pressing Back landed on an empty shop and the shopper had to type it again.
  //
  // replaceState rather than pushState: typing must not push a history entry per
  // keystroke, but it does leave ?q= on the entry we navigate AWAY from, which is what
  // Back returns to. Side benefit: a search result becomes a shareable link.
  const [search, setSearch] = useState(
    () => new URLSearchParams(window.location.search).get('q') || ''
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    if (search.trim()) url.searchParams.set('q', search);
    else url.searchParams.delete('q');
    window.history.replaceState(null, '', url.toString());
  }, [search]);
  const [visibleCount, setVisibleCount] = useState(24);
  const observerRef = React.useRef<IntersectionObserver | null>(null);

  // callback ref: fires exactly when the sentinel node mounts/unmounts, so the
  // observer always attaches on the first render (a plain useRef + effect misses
  // the first mount because ref.current is not reactive).
  const sentinelRef = React.useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (node) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) setVisibleCount((n) => n + 24);
      }, { rootMargin: '600px' });
      observerRef.current.observe(node);
    }
  }, []);

  // reset to first page whenever the filter changes
  useEffect(() => { setVisibleCount(24); }, [filterSlug, mode, search]);

  useEffect(() => {
    let alive = true;
    if (!seeded) setLoading(true);   // seeded: keep the grid on screen while refreshing
    setError(false);
    (async () => {
      try {
        const list = await fetchProducts('grid');
        if (!alive) return;
        setProducts(list);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // title + filtered set
  const { title, shown } = useMemo(() => {
    if (!filterSlug) return { title: 'All Products', shown: products };
    if (mode === 'collection') {
      const match = products.filter(p =>
        (p.collections || []).some(c => slugify(c) === filterSlug));
      const label = match[0]?.collections?.find(c => slugify(c) === filterSlug) || filterSlug;
      return { title: label, shown: match };
    }
    if (mode === 'group') {
      // a group maps to several child categories (shopConfig.category_groups)
      const g = (cfg.category_groups || []).find(x => slugify(x.group) === filterSlug);
      if (!g) return { title: filterSlug.replace(/-/g, ' '), shown: [] };
      const catSet = new Set(g.categories);
      return { title: g.group, shown: products.filter(p => catSet.has(p.category)) };
    }
    const match = products.filter(p => slugify(p.category) === filterSlug);
    const label = match[0]?.category || filterSlug.replace(/-/g, ' ');
    return { title: label, shown: match };
  }, [products, filterSlug, mode]);

  // search narrows whatever the category/collection filter already produced
  const visible = useMemo(
    () => (search.trim() ? shown.filter((p) => matchesSearch(p, search)) : shown),
    [shown, search]
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px 48px' }}>
      <style>{`
        .prod-grid { display:grid; gap:14px; grid-template-columns:repeat(2,1fr); }
        @media(min-width:640px){ .prod-grid { grid-template-columns:repeat(3,1fr); gap:18px } }
        @media(min-width:960px){ .prod-grid { grid-template-columns:repeat(4,1fr); gap:22px } }
        .prod-card { background:#fff; border:1px solid rgba(0,0,0,.06); cursor:pointer;
          display:flex; flex-direction:column; box-shadow:0 2px 10px rgba(0,0,0,.10), 0 1px 3px rgba(0,0,0,.08);
          transition:transform .18s, box-shadow .18s; }
        .prod-card:hover { transform:translateY(-3px); box-shadow:0 10px 32px rgba(0,0,0,.16) }
        .prod-img { aspect-ratio:3/4; width:100%; object-fit:cover; background:#f2efe6; display:block }
      `}</style>

      <h1 style={{ fontSize: 'clamp(20px,5vw,30px)', fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '4px 0 4px' }}>
        {title}
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 20px' }}>
        <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(0,0,0,.4)', margin: 0 }}>
          {loading ? 'Loading...' : `${visible.length} ${visible.length === 1 ? 'item' : 'items'}`}
        </p>
        <div style={{ position: 'relative' }}>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, category or brand"
            aria-label="Search products"
            style={{ background: '#fff', border: '1px solid rgba(0,0,0,.18)', padding: '9px 14px',
              fontSize: 13, outline: 'none', width: 'min(260px, 60vw)', fontFamily: 'inherit' }} />
        </div>
      </div>

      {error && (
        <p style={{ color: accent, fontSize: 14 }}>Could not load products just now. Please refresh.</p>
      )}

      {loading && !products.length && (
        <div className="prod-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="prod-card"><div className="prod-img" style={{ opacity: .5 }} /></div>
          ))}
        </div>
      )}

      {!loading && !visible.length && !error && (
        <p style={{ color: 'rgba(0,0,0,.5)', fontSize: 15 }}>
          {search.trim() ? `Nothing matches "${search}".` : 'No products in this category yet.'}
        </p>
      )}

      <div className="prod-grid">
        {visible.slice(0, visibleCount).map((p) => (
          <ProductCard key={p.product_id} p={p} accent={accent} navigate={navigate} />
        ))}
      </div>
      {visibleCount < visible.length && (
        // key forces a remount on each growth: IntersectionObserver only reports a
        // threshold crossing, so a sentinel that stays inside the band is never
        // reported again. Remounting re-observes it and yields a fresh entry.
        <div key={visibleCount} ref={sentinelRef} style={{ height: 1 }} />
      )}
    </div>
  );
};
